import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ArrowLeft, ShieldCheck, Sparkles, CheckCircle2, FileText as FileTextIcon, HelpCircle, Copy, AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RiskBadge } from "./RiskBadge";
import wordIcon from "@/assets/word-icon.png";
import pdfIcon from "@/assets/pdf-icon.png";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FALLBACK_RA_TEMPLATES, type FallbackTemplate } from "./fallbackTemplates";

/** Label with a hover tooltip explaining the field. */
function HelpLabel({ children, tip, side = "right" }: { children: React.ReactNode; tip: string; side?: "top" | "right" | "bottom" | "left" }) {
  return (
    <div className="flex items-center gap-1.5 pt-2 mb-1">
      <Label className="m-0">{children}</Label>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              tabIndex={-1}
              onFocus={(e) => e.currentTarget.blur()}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Help"
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed">
            {tip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

type Hazard = {
  id?: string;
  position: number;
  hazard: string;
  who_at_risk: string;
  existing_controls: string;
  likelihood: number;
  severity: number;
  additional_controls: string;
  residual_likelihood: number;
  residual_severity: number;
  action_owner: string;
  action_due_date: string | null;
};

type Assessment = {
  id: string;
  pta_id: string;
  event_id: string | null;
  title: string;
  assessment_ref: string | null;
  description: string | null;
  activity_type: string | null;
  assessment_type: "event" | "generic";
  location: string | null;
  assessment_date: string | null;
  review_date: string | null;
  assessor_name: string | null;
  general_notes: string | null;
  emergency_procedures: string | null;
  first_aid_notes: string | null;
  status: "draft" | "pending_approval" | "approved" | "needs_review" | "archived";
  version: number;
  approved_by_name: string | null;
  approved_at: string | null;
  updated_at: string;
  isNew?: boolean;
};

const ASSESSMENT_TYPE_LABEL: Record<"event" | "generic", string> = {
  event: "Event Risk Assessment (One-Off Events)",
  generic: "Generic Risk Assessment (Fixed / Regular Activities)",
};

const scoreBand = (s: number) => {
  if (s >= 15) return { label: "High", className: "bg-red-100 text-red-800 border-red-300" };
  if (s >= 8) return { label: "Medium", className: "bg-amber-100 text-amber-800 border-amber-300" };
  return { label: "Low", className: "bg-green-100 text-green-800 border-green-300" };
};

const emptyHazard = (position: number): Hazard => ({
  position, hazard: "", who_at_risk: "", existing_controls: "",
  likelihood: 1, severity: 1, additional_controls: "",
  residual_likelihood: 1, residual_severity: 1,
  action_owner: "", action_due_date: null,
});

interface Props {
  ptaId: string;
  events: any[];
  ptaInfo: { name: string; logo_url: string | null } | null;
}

export default function RiskAssessmentsTab({ ptaId, events, ptaInfo }: Props) {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [members, setMembers] = useState<{ id: string; full_name: string; role: string | null }[]>([]);
  const [requireRaApproval, setRequireRaApproval] = useState(true);
  const [editing, setEditing] = useState<Assessment | null>(null);
  const [hazards, setHazards] = useState<Hazard[]>([]);
  const [editingHazardIndex, setEditingHazardIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const loadList = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("risk_assessments")
      .select("*")
      .eq("pta_id", ptaId)
      .order("updated_at", { ascending: false });
    setAssessments((data as any) ?? []);
    setLoading(false);
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    const { data, error } = await supabase
      .from("risk_assessment_templates")
      .select("*")
      .order("display_order", { ascending: true, nullsFirst: false })
      .order("name");
    if (error) {
      console.error("[RA templates] load failed:", error);
      toast.error(`Failed to load templates: ${error.message}`);
    }
    const rows = data ?? [];
    // Always ensure the built-in system templates are shown. If the database
    // returned no system templates (RLS, empty seed, etc.), fall back to the
    // bundled client-side list so the tab is never blank.
    const hasSystem = rows.some((r: any) => r.is_system);
    const merged = hasSystem ? rows : [...rows, ...FALLBACK_RA_TEMPLATES];
    setTemplates(merged);
    setTemplatesLoading(false);
  };

  const loadMembers = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from("members")
      .select("id, full_name, role")
      .eq("pta_id", ptaId)
      .order("full_name");
    setMembers((data as any) ?? []);
  };

  const loadPolicy = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from("trustee_policies")
      .select("require_ra_approval")
      .eq("pta_id", ptaId)
      .maybeSingle();
    setRequireRaApproval(((data as any)?.require_ra_approval) ?? true);
  };

  useEffect(() => { if (ptaId) { loadList(); loadTemplates(); loadMembers(); loadPolicy(); } }, [ptaId]);


  const openEditor = async (a: Assessment) => {
    setEditing(a);
    const { data } = await supabase
      .from("risk_assessment_hazards")
      .select("*")
      .eq("assessment_id", a.id)
      .order("position");
    setHazards((data as any) ?? []);
  };

  const makeDraft = (overrides: Partial<Assessment> = {}): Assessment => ({
    id: "",
    pta_id: ptaId ?? "",
    event_id: null,
    title: "New Risk Assessment",
    assessment_ref: null,
    description: null,
    activity_type: null,
    assessment_type: "event",
    location: null,
    assessment_date: null,
    review_date: null,
    assessor_name: null,
    general_notes: null,
    emergency_procedures: null,
    first_aid_notes: null,
    status: "draft",
    version: 1,
    approved_by_name: null,
    approved_at: null,
    updated_at: new Date().toISOString(),
    isNew: true,
    ...overrides,
  });

  const createBlank = async () => {
    setEditing(makeDraft());
    setHazards([]);
  };

  const createFromTemplate = async (templateId: string) => {
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    const isFallback = typeof templateId === "string" && templateId.startsWith("fallback-");

    let sourceHazards: any[] = [];
    if (isFallback) {
      sourceHazards = (tpl as FallbackTemplate).hazards ?? [];
    } else {
      const { data: th } = await supabase
        .from("risk_assessment_template_hazards")
        .select("*")
        .eq("template_id", templateId)
        .order("position");
      sourceHazards = th ?? [];
    }

    setEditing(makeDraft({ title: tpl.name, activity_type: tpl.activity_type ?? null }));
    setHazards(
      sourceHazards.map((h: any, idx: number) => ({
        id: `tmp-${idx}-${Date.now()}`,
        assessment_id: "",
        position: h.position ?? idx,
        hazard: h.hazard,
        who_at_risk: h.who_at_risk,
        existing_controls: h.existing_controls,
        likelihood: h.likelihood,
        severity: h.severity,
        additional_controls: h.additional_controls,
        residual_likelihood: h.residual_likelihood,
        residual_severity: h.residual_severity,
        action_owner: h.action_owner ?? null,
        action_due_date: h.action_due_date ?? null,
      })) as any
    );
    toast.success(`Created from "${tpl.name}" template`);
  };

  const saveAssessment = async () => {
    if (!editing) return;
    const ref = editing.assessment_ref?.trim() || null;

    if (editing.isNew) {
      const { data: ins, error } = await supabase
        .from("risk_assessments")
        .insert({
          pta_id: ptaId,
          created_by: user?.id,
          title: editing.title,
          assessment_ref: ref,
          description: editing.description,
          activity_type: editing.activity_type,
          assessment_type: editing.assessment_type,
          location: editing.location,
          event_id: editing.event_id,
          assessment_date: editing.assessment_date,
          review_date: editing.review_date,
          assessor_name: editing.assessor_name,
          general_notes: editing.general_notes,
          emergency_procedures: editing.emergency_procedures,
          first_aid_notes: editing.first_aid_notes,
          status: "draft",
        } as any)
        .select()
        .single();
      if (error) {
        if ((error as any).code === "23505") {
          toast.error("This Assessment ID is already used in your PTA");
        } else {
          toast.error(error.message);
        }
        return;
      }
      const newId = (ins as any).id as string;
      if (hazards.length) {
        const rows = hazards.map((h, idx) => ({
          assessment_id: newId,
          position: idx,
          hazard: h.hazard,
          who_at_risk: h.who_at_risk,
          existing_controls: h.existing_controls,
          likelihood: h.likelihood,
          severity: h.severity,
          additional_controls: h.additional_controls,
          residual_likelihood: h.residual_likelihood,
          residual_severity: h.residual_severity,
          action_owner: h.action_owner,
          action_due_date: h.action_due_date,
        }));
        await supabase.from("risk_assessment_hazards").insert(rows);
      }
      setEditing({ ...editing, id: newId, isNew: false });
      toast.success("Saved");
      await loadList();
      return;
    }

    const { error } = await supabase
      .from("risk_assessments")
      .update({
        title: editing.title,
        assessment_ref: ref,
        description: editing.description,
        activity_type: editing.activity_type,
        assessment_type: editing.assessment_type,
        location: editing.location,
        event_id: editing.event_id,
        assessment_date: editing.assessment_date,
        review_date: editing.review_date,
        assessor_name: editing.assessor_name,
        general_notes: editing.general_notes,
        emergency_procedures: editing.emergency_procedures,
        first_aid_notes: editing.first_aid_notes,
        status: editing.status === "approved" ? "draft" : editing.status,
      } as any)
      .eq("id", editing.id);
    if (error) {
      if ((error as any).code === "23505") {
        toast.error("This Assessment ID is already used in your PTA");
      } else {
        toast.error(error.message);
      }
      return;
    }
    await supabase.from("risk_assessment_hazards").delete().eq("assessment_id", editing.id);
    if (hazards.length) {
      const rows = hazards.map((h, idx) => ({
        assessment_id: editing.id,
        position: idx,
        hazard: h.hazard,
        who_at_risk: h.who_at_risk,
        existing_controls: h.existing_controls,
        likelihood: h.likelihood,
        severity: h.severity,
        additional_controls: h.additional_controls,
        residual_likelihood: h.residual_likelihood,
        residual_severity: h.residual_severity,
        action_owner: h.action_owner,
        action_due_date: h.action_due_date,
      }));
      await supabase.from("risk_assessment_hazards").insert(rows);
    }
    toast.success("Saved");
    await loadList();
  };

  const duplicateAssessment = async (a: Assessment) => {
    const { data: src } = await supabase
      .from("risk_assessments")
      .select("*")
      .eq("id", a.id)
      .single();
    if (!src) { toast.error("Could not load source"); return; }
    const { data: srcH } = await supabase
      .from("risk_assessment_hazards")
      .select("*")
      .eq("assessment_id", a.id)
      .order("position");
    const { id, created_at, updated_at, approved_at, approved_by_user_id, approved_by_name, approved_by_role, ...rest } = src as any;
    const { data: newRa, error } = await supabase
      .from("risk_assessments")
      .insert({
        ...rest,
        title: `${src.title} (Copy)`,
        assessment_ref: null,
        status: "draft",
        created_by: user?.id,
      })
      .select()
      .single();
    if (error) { toast.error(error.message); return; }
    if (srcH && srcH.length) {
      const rows = (srcH as any[]).map((h) => {
        const { id: _hid, assessment_id: _aid, created_at: _c, updated_at: _u, ...hrest } = h;
        return { ...hrest, assessment_id: newRa.id };
      });
      await supabase.from("risk_assessment_hazards").insert(rows);
    }
    toast.success("Duplicated");
    await loadList();
    openEditor(newRa as any);
  };

  const submitForApproval = async () => {
    if (!editing) return;
    await saveAssessment();
    await supabase.from("risk_assessments").update({ status: "pending_approval" }).eq("id", editing.id);
    toast.success("Submitted for approval");
    await loadList();
    setEditing({ ...editing, status: "pending_approval" });
  };

  const approve = async () => {
    if (!editing) return;
    // Check role chair/trustee
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", user?.id);
    const can = (roles ?? []).some((r: any) => r.role === "chair" || r.role === "trustee" || r.role === "site_admin");
    if (!can) { toast.error("Only Chair or Trustee can approve"); return; }
    const { data: prof } = await supabase.from("profiles").select("full_name").eq("id", user?.id).maybeSingle();
    const role = (roles ?? []).find((r: any) => r.role === "chair") ? "Chair" : "Trustee";
    const { error } = await supabase.from("risk_assessments").update({
      status: "approved",
      approved_by_user_id: user?.id,
      approved_by_name: prof?.full_name ?? "Approver",
      approved_by_role: role,
      approved_at: new Date().toISOString(),
    }).eq("id", editing.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Approved");
    await loadList();
    setEditing({ ...editing, status: "approved", approved_by_name: prof?.full_name ?? "Approver", approved_at: new Date().toISOString() });
  };

  const deleteAssessment = async (id: string) => {
    const { error } = await supabase.from("risk_assessments").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Deleted");
    setConfirmDeleteId(null);
    await loadList();
  };

  const aiSuggest = async () => {
    if (!editing) return;
    setAiBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-ra-hazards", {
        body: { activity: editing.activity_type || editing.title, title: editing.title },
      });
      if (error) throw error;
      const suggested = (data?.hazards ?? []) as Hazard[];
      const start = hazards.length;
      setHazards([...hazards, ...suggested.map((h, i) => ({ ...emptyHazard(start + i), ...h }))]);
      toast.success(`Added ${suggested.length} suggested hazards`);
    } catch (e: any) {
      toast.error(e?.message || "AI suggestion failed");
    } finally {
      setAiBusy(false);
    }
  };

  const fetchImageAsDataUrl = async (url: string): Promise<{ dataUrl: string; width: number; height: number; format: "PNG" | "JPEG" } | null> => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      const dataUrl: string = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      const dims = await new Promise<{ w: number; h: number }>((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ w: 0, h: 0 });
        img.src = dataUrl;
      });
      const format: "PNG" | "JPEG" = blob.type.includes("jpeg") || blob.type.includes("jpg") ? "JPEG" : "PNG";
      return { dataUrl, width: dims.w, height: dims.h, format };
    } catch {
      return null;
    }
  };

  const bandColor = (label: string): [number, number, number] => {
    if (label === "High") return [220, 38, 38];
    if (label === "Medium") return [234, 179, 8];
    return [34, 197, 94];
  };

  const exportPDF = async () => {
    if (!editing) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const marginX = 12;

    // Brand blue (matches site primary HSL 214 84% 56%)
    const brandBlue: [number, number, number] = [49, 130, 237];

    // Transparent header (no fill) — logo + title only
    let textStartX = marginX;
    let headerH = 18;
    if (ptaInfo?.logo_url) {
      const img = await fetchImageAsDataUrl(ptaInfo.logo_url);
      if (img && img.width && img.height) {
        const maxH = 18;
        const w = (img.width / img.height) * maxH;
        try {
          doc.addImage(img.dataUrl, img.format, marginX, 6, w, maxH);
          textStartX = marginX + w + 6;
          headerH = maxH + 6;
        } catch { /* ignore bad image */ }
      }
    }

    doc.setTextColor(20, 20, 20);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Risk Assessment", textStartX, 14);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(editing.title, textStartX, 22, { maxWidth: pageW - textStartX - marginX });

    // Metadata block — top right
    const linkedEvent = editing.assessment_type === "event" && editing.event_id
      ? events.find((e: any) => e.id === editing.event_id)
      : null;
    const metaLines: string[] = [];
    if (editing.assessment_ref) metaLines.push(`ID: ${editing.assessment_ref}`);
    if (editing.activity_type) metaLines.push(`Activity: ${editing.activity_type}`);
    if (linkedEvent?.name) metaLines.push(`Event: ${linkedEvent.name}`);
    if (linkedEvent?.event_date) metaLines.push(`Event date: ${linkedEvent.event_date}`);
    if (linkedEvent?.location) metaLines.push(`Event location: ${linkedEvent.location}`);
    if (editing.location) metaLines.push(`Location: ${editing.location}`);
    if (editing.assessment_date) metaLines.push(`Date: ${editing.assessment_date}`);
    if (editing.assessment_type !== "event" && editing.review_date) metaLines.push(`Review: ${editing.review_date}`);
    if (editing.assessor_name) metaLines.push(`Assessor: ${editing.assessor_name}`);
    if (metaLines.length) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      metaLines.forEach((line, i) => doc.text(line, pageW - marginX, 14 + i * 4, { align: "right" }));
      headerH = Math.max(headerH, 14 + metaLines.length * 4);
    }

    let nextY = headerH + 6;


    // Description above the table
    if (editing.description) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(80, 80, 80);
      doc.text("Description", marginX, nextY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(40, 40, 40);
      const lines = doc.splitTextToSize(editing.description, pageW - marginX * 2);
      doc.text(lines, marginX, nextY + 4);
      nextY += 4 + lines.length * 3.2 + 3;
    }

    // Group tints (Inherent lighter, Residual a touch darker). Header bar = brandBlue.
    const inherentTint: [number, number, number] = [239, 246, 255];
    const inherentTintAlt: [number, number, number] = [219, 234, 254];
    const residualTint: [number, number, number] = [219, 234, 254];
    const residualTintAlt: [number, number, number] = [191, 219, 254];

    const neutralHead: [number, number, number] = [107, 114, 128]; // grey-500 for Responsible

    // Column indices (body):
    //  0 Hazard | 1 Who | 2 In:Controls | 3 In:L | 4 In:S | 5 In:Score
    //  6 Re:AddControls | 7 Re:L | 8 Re:S | 9 Re:Score | 10 Responsible
    const DIAG_COLS = [3, 4, 5, 7, 8, 9];
    const INHERENT_BODY_COLS = [2, 3, 4, 5];
    const RESIDUAL_BODY_COLS = [6, 7, 8, 9];
    const LAST_COL = 10;

    autoTable(doc, {
      startY: nextY,
      head: [
        [
          { content: "Hazard:", rowSpan: 2, styles: { valign: "bottom" } },
          { content: "Who at risk:", rowSpan: 2, styles: { valign: "bottom" } },
          { content: "Inherent:", colSpan: 4, styles: { halign: "center", valign: "bottom", minCellHeight: 5, cellPadding: { top: 1.5, bottom: 0.5, left: 2, right: 2 } } },
          { content: "Residual:", colSpan: 4, styles: { halign: "center", valign: "bottom", minCellHeight: 5, cellPadding: { top: 1.5, bottom: 0.5, left: 2, right: 2 } } },
          { content: "Responsible:", rowSpan: 2, styles: { valign: "bottom", fillColor: neutralHead } },
        ],
        [
          { content: "Controls:", styles: { valign: "bottom", minCellHeight: 18 } },
          { content: "Likelihood:", styles: { minCellHeight: 18 } },
          { content: "Severity:", styles: { minCellHeight: 18 } },
          { content: "Score:", styles: { minCellHeight: 18 } },
          { content: "Additional controls:", styles: { valign: "bottom", minCellHeight: 18 } },
          { content: "Likelihood:", styles: { minCellHeight: 18 } },
          { content: "Severity:", styles: { minCellHeight: 18 } },
          { content: "Score:", styles: { minCellHeight: 18 } },
        ],
      ] as any,
      body: hazards.map((h) => {
        const inhScore = h.likelihood * h.severity;
        const resScore = h.residual_likelihood * h.residual_severity;
        const bandFromNum = (n: number) => (n >= 5 ? "High" : n >= 3 ? "Medium" : "Low");
        return [
          h.hazard, h.who_at_risk,
          h.existing_controls ?? "",
          bandFromNum(h.likelihood),
          bandFromNum(h.severity),
          scoreBand(inhScore).label,
          h.additional_controls ?? "",
          bandFromNum(h.residual_likelihood),
          bandFromNum(h.residual_severity),
          scoreBand(resScore).label,
          h.action_owner ?? "",
        ];
      }),
      styles: { fontSize: 7.5, cellPadding: 2, valign: "top", lineColor: [220, 220, 220], lineWidth: 0 },
      tableLineColor: [220, 220, 220],
      tableLineWidth: 0.3,
      headStyles: { fillColor: brandBlue, textColor: 255, fontStyle: "bold", halign: "center", fontSize: 7, cellPadding: 1.5, overflow: "visible" },
      alternateRowStyles: { fillColor: [243, 244, 246] },
      columnStyles: {
        3: { halign: "center", cellWidth: 10, cellPadding: { top: 2, bottom: 2, left: 0.5, right: 0.5 } },
        4: { halign: "center", cellWidth: 10, cellPadding: { top: 2, bottom: 2, left: 0.5, right: 0.5 } },
        5: { halign: "center", cellWidth: 12, fontStyle: "bold", cellPadding: { top: 2, bottom: 2, left: 0.5, right: 0.5 } },
        7: { halign: "center", cellWidth: 10, cellPadding: { top: 2, bottom: 2, left: 0.5, right: 0.5 } },
        8: { halign: "center", cellWidth: 10, cellPadding: { top: 2, bottom: 2, left: 0.5, right: 0.5 } },
        9: { halign: "center", cellWidth: 12, fontStyle: "bold", cellPadding: { top: 2, bottom: 2, left: 0.5, right: 0.5 } },
        10: { cellWidth: 36 },
      },
      margin: { left: marginX, right: marginX },
      willDrawCell: (data) => {
        // Render L/S/Score sub-headers diagonally at 60° for both groups
        if (
          data.section === "head" &&
          data.row.index === 1 &&
          DIAG_COLS.includes(data.column.index)
        ) {
          const { x, y, width, height } = data.cell;
          doc.setFillColor(brandBlue[0], brandBlue[1], brandBlue[2]);
          doc.rect(x, y, width, height, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          const txt = String((data.cell.raw as any)?.content ?? data.cell.text?.[0] ?? "");
          // Anchor near bottom-left of cell, steeper angle so text stays inside cell horizontally
          doc.text(txt, x + 2, y + height - 1.5, { angle: 60, align: "left" });
          return false;
        }
        return true;
      },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        if (data.column.index < LAST_COL) {
          const { x, y, width, height } = data.cell;
          doc.setDrawColor(150, 150, 150);
          doc.setLineWidth(0.4);
          doc.line(x + width, y + 1.5, x + width, y + height - 1.5);
        }
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const h = hazards[data.row.index];
        if (!h) return;
        const odd = data.row.index % 2 === 1;
        if (INHERENT_BODY_COLS.includes(data.column.index)) {
          data.cell.styles.fillColor = odd ? inherentTintAlt : inherentTint;
        } else if (RESIDUAL_BODY_COLS.includes(data.column.index)) {
          data.cell.styles.fillColor = odd ? residualTintAlt : residualTint;
        }
        if (data.column.index === 5) {
          data.cell.styles.textColor = bandColor(scoreBand(h.likelihood * h.severity).label);
        } else if (data.column.index === 9) {
          data.cell.styles.textColor = bandColor(scoreBand(h.residual_likelihood * h.residual_severity).label);
        }
      },
    });




    const extras: string[][] = [];
    if (editing.general_notes) extras.push(["General notes", editing.general_notes]);
    if (editing.emergency_procedures) extras.push(["Emergency procedures", editing.emergency_procedures]);
    if (editing.first_aid_notes) extras.push(["First aid", editing.first_aid_notes]);
    if (extras.length) {
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 6,
        body: extras,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3, lineColor: [220, 220, 220] },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 50, fillColor: [243, 244, 246] } },
        margin: { left: marginX, right: marginX },
      });
    }

    // Footer on every page
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setDrawColor(220, 220, 220);
      doc.line(marginX, pageH - 10, pageW - marginX, pageH - 10);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      const footL = `${ptaInfo?.name ?? ""}${ptaInfo?.name ? " — " : ""}Risk Assessment`;
      doc.text(footL, marginX, pageH - 5);
      const footR = `Page ${p} of ${pageCount}  •  Generated ${new Date().toLocaleDateString()}`;
      doc.text(footR, pageW - marginX, pageH - 5, { align: "right" });
    }

    doc.save(`${editing.title.replace(/\s+/g, "_")}_risk_assessment.pdf`);
  };

  const exportWord = async () => {
    if (!editing) return;

    let logoDataUrl: string | null = null;
    if (ptaInfo?.logo_url) {
      const img = await fetchImageAsDataUrl(ptaInfo.logo_url);
      if (img) logoDataUrl = img.dataUrl;
    }

    const brandBlue = "#3182ed";
    const neutralGrey = "#6b7280";
    const inherentTint = "#eff6ff";
    const inherentTintAlt = "#dbeafe";
    const residualTint = "#dbeafe";
    const residualTintAlt = "#bfdbfe";
    const cellBorders = "border-top:1px solid #d1d5db;border-bottom:1px solid #d1d5db;";
    const cellStyle = `${cellBorders}padding:6px 8px;font-family:Arial,sans-serif;font-size:9pt;vertical-align:top;position:relative;`;
    const thBase = `${cellBorders}padding:4px 6px;font-family:Arial,sans-serif;font-size:8pt;color:#fff;font-weight:bold;text-align:center;white-space:nowrap;vertical-align:bottom;position:relative;`;
    const thBlue = `${thBase}background:${brandBlue};`;
    const thGrey = `${thBase}background:${neutralGrey};`;
    const thBlueTop = `${cellBorders}padding:4px 6px 0;height:14px;line-height:10px;font-family:Arial,sans-serif;font-size:8pt;background:${brandBlue};color:#fff;font-weight:bold;text-align:center;white-space:nowrap;vertical-align:bottom;position:relative;`;
    const thBlueDiag = `${cellBorders}padding:0;font-family:Arial,sans-serif;font-size:8pt;background:${brandBlue};color:#fff;font-weight:bold;height:60px;width:28px;position:relative;`;
    const diagSpan = `position:absolute;left:4px;bottom:6px;transform:rotate(-60deg);-ms-transform:rotate(-60deg);transform-origin:bottom left;white-space:nowrap;`;
    const divR = `<div style="position:absolute;top:2px;bottom:2px;right:0;width:2px;background:#9ca3af;"></div>`;
    const bandText = (label: string) =>
      label === "High" ? "#dc2626" : label === "Medium" ? "#b45309" : "#15803d";
    const bandFromNum = (n: number) => (n >= 5 ? "High" : n >= 3 ? "Medium" : "Low");

    const rows = hazards.map((h, i) => {
      const inhScore = h.likelihood * h.severity;
      const resScore = h.residual_likelihood * h.residual_severity;
      const inhLbl = scoreBand(inhScore).label;
      const resLbl = scoreBand(resScore).label;
      const odd = i % 2 === 1;
      const baseBg = odd ? "#f9fafb" : "#ffffff";
      const inhBg = odd ? inherentTintAlt : inherentTint;
      const resBg = odd ? residualTintAlt : residualTint;
      const rowCell = cellStyle + `background:${baseBg};`;
      const scoreCellStyle = `${cellBorders}padding:6px 1px;font-family:Arial,sans-serif;font-size:9pt;vertical-align:top;position:relative;`;
      const inhCell = scoreCellStyle + `background:${inhBg};text-align:center;`;
      const resCell = scoreCellStyle + `background:${resBg};text-align:center;`;
      return `<tr>
        <td style="${rowCell}">${escapeHtml(h.hazard)}${divR}</td>
        <td style="${rowCell}">${escapeHtml(h.who_at_risk)}${divR}</td>
        <td style="${cellStyle}background:${inhBg};">${escapeHtml(h.existing_controls ?? "")}${divR}</td>
        <td style="${inhCell}">${bandFromNum(h.likelihood)}${divR}</td>
        <td style="${inhCell}">${bandFromNum(h.severity)}${divR}</td>
        <td style="${inhCell}color:${bandText(inhLbl)};font-weight:bold;">${inhLbl}${divR}</td>
        <td style="${cellStyle}background:${resBg};">${escapeHtml(h.additional_controls ?? "")}${divR}</td>
        <td style="${resCell}">${bandFromNum(h.residual_likelihood)}${divR}</td>
        <td style="${resCell}">${bandFromNum(h.residual_severity)}${divR}</td>
        <td style="${resCell}color:${bandText(resLbl)};font-weight:bold;">${resLbl}${divR}</td>
        <td style="${rowCell}">${escapeHtml(h.action_owner ?? "")}</td>
      </tr>`;
    }).join("");



    const logoHtml = logoDataUrl
      ? `<img src="${logoDataUrl}" alt="" style="max-height:70px;max-width:200px;vertical-align:middle;margin-right:16px;" />`
      : "";




    const metaRows: string[] = [];
    const addMeta = (label: string, value: string | null | undefined) => {
      if (!value) return;
      metaRows.push(`<div><span style="color:#6b7280;">${label}:</span> <span style="color:#374151;">${escapeHtml(value)}</span></div>`);
    };
    const linkedEventW = editing.assessment_type === "event" && editing.event_id
      ? events.find((e: any) => e.id === editing.event_id)
      : null;
    addMeta("ID", editing.assessment_ref);
    addMeta("Type", ASSESSMENT_TYPE_LABEL[editing.assessment_type] ?? null);
    addMeta("Activity", editing.activity_type);
    if (linkedEventW) {
      addMeta("Event", linkedEventW.name);
      addMeta("Event date", linkedEventW.event_date);
      addMeta("Event location", linkedEventW.location);
    }
    addMeta("Location", editing.location);
    addMeta("Date", editing.assessment_date);
    if (editing.assessment_type !== "event") addMeta("Review", editing.review_date);
    addMeta("Assessor", editing.assessor_name);
    const metaHtml = metaRows.length
      ? `<td style="padding:22px 4px 8px 4px;vertical-align:top;text-align:right;font-family:Arial,sans-serif;font-size:8.5pt;line-height:1.4;">${metaRows.join("")}</td>`
      : "";

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="utf-8"><title>Risk Assessment</title>
    <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom>
    <w:DoNotOptimizeForBrowser/></w:WordDocument></xml><![endif]-->
    <style>@page { size: A4 landscape; margin: 1.5cm; } body { font-family: Arial, sans-serif; color:#111827; }</style>
    </head><body>
      <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
        <tr>
          <td style="padding:8px 4px;vertical-align:middle;width:1%;">${logoHtml}</td>
          <td style="padding:8px 4px;vertical-align:middle;color:#111827;">
            <div style="font-size:11pt;color:#6b7280;font-family:Arial,sans-serif;">Risk Assessment</div>
            <div style="font-size:18pt;font-weight:bold;font-family:Arial,sans-serif;">${escapeHtml(editing.title)}</div>
          </td>
          ${metaHtml}
        </tr>
      </table>

      ${editing.description ? `<p style="font-family:Arial,sans-serif;font-size:8.5pt;margin:0 0 10px 0;color:#374151;"><strong style="color:#6b7280;">Description:</strong> ${escapeHtml(editing.description)}</p>` : ""}

      <table style="border-collapse:collapse;width:100%;border:1px solid #d1d5db;">
        <thead>
          <tr>
            <th style="${thBlue}" rowspan="2">Hazard:</th>
            <th style="${thBlue}" rowspan="2">Who at risk:</th>
            <th style="${thBlueTop}" colspan="4">Inherent:</th>
            <th style="${thBlueTop}" colspan="4">Residual:</th>
            <th style="${thGrey}" rowspan="2">Responsible:</th>
          </tr>
          <tr>
            <th style="${thBlue}">Controls:</th>
            <th style="${thBlueDiag}"><span style="${diagSpan}">Likelihood:</span></th>
            <th style="${thBlueDiag}"><span style="${diagSpan}">Severity:</span></th>
            <th style="${thBlueDiag}"><span style="${diagSpan}">Score:</span></th>
            <th style="${thBlue}">Additional controls:</th>
            <th style="${thBlueDiag}"><span style="${diagSpan}">Likelihood:</span></th>
            <th style="${thBlueDiag}"><span style="${diagSpan}">Severity:</span></th>
            <th style="${thBlueDiag}"><span style="${diagSpan}">Score:</span></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>



      ${editing.general_notes ? `<h3 style="font-family:Arial;color:${brandBlue};margin-top:18px;">General notes</h3><p style="font-family:Arial;font-size:10pt;">${escapeHtml(editing.general_notes)}</p>` : ""}
      ${editing.emergency_procedures ? `<h3 style="font-family:Arial;color:${brandBlue};margin-top:18px;">Emergency procedures</h3><p style="font-family:Arial;font-size:10pt;">${escapeHtml(editing.emergency_procedures)}</p>` : ""}
      ${editing.first_aid_notes ? `<h3 style="font-family:Arial;color:${brandBlue};margin-top:18px;">First aid</h3><p style="font-family:Arial;font-size:10pt;">${escapeHtml(editing.first_aid_notes)}</p>` : ""}

      <p style="margin-top:24px;font-size:8pt;color:#6b7280;border-top:1px solid #d1d5db;padding-top:6px;font-family:Arial;">
        ${ptaInfo?.name ? escapeHtml(ptaInfo.name) + " — " : ""}Risk Assessment &nbsp;•&nbsp; Generated ${new Date().toLocaleDateString()}
      </p>
    </body></html>`;

    const blob = new Blob(["\ufeff", html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${editing.title.replace(/\s+/g, "_")}_risk_assessment.doc`;
    a.click(); URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => {
    const bands = { Low: 0, Medium: 0, High: 0 };
    let highest = 0, highestRes = 0;
    hazards.forEach(h => {
      const s = h.likelihood * h.severity;
      const r = h.residual_likelihood * h.residual_severity;
      bands[scoreBand(r).label as keyof typeof bands]++;
      if (s > highest) highest = s;
      if (r > highestRes) highestRes = r;
    });
    return { bands, highest, highestRes };
  }, [hazards]);

  // ============================================================
  // EDITOR VIEW
  // ============================================================
  if (editing) {
    const isLocked = editing.status === "approved";
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => { setEditing(null); setHazards([]); }}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to library
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            {requireRaApproval && (
              <Badge variant="outline">{editing.status.replace("_", " ")}</Badge>
            )}
            {requireRaApproval && editing.approved_by_name && (
              <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Approved by {editing.approved_by_name}
              </Badge>
            )}
            <Button size="sm" variant="outline" onClick={exportPDF}>
              <img src={pdfIcon} alt="" className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={exportWord}>
              <img src={wordIcon} alt="" className="h-4 w-4 mr-1" /> Word
            </Button>
            {requireRaApproval && editing.status === "pending_approval" && (
              <Button size="sm" onClick={approve}>
                <ShieldCheck className="h-4 w-4 mr-1" /> Approve
              </Button>
            )}
            {requireRaApproval && editing.status === "draft" && (
              <Button size="sm" variant="secondary" onClick={submitForApproval}>Submit for approval</Button>
            )}
            <Button size="sm" onClick={saveAssessment} disabled={isLocked}>Save</Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6 grid gap-3 md:grid-cols-2">
            <div>
              <HelpLabel tip="Short name for the activity. e.g. 'Summer Fair – Bouncy Castle' or 'Christmas Disco'.">Title</HelpLabel>
              <Input value={editing.title} disabled={isLocked}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <div>
              <HelpLabel side="left" tip="A unique reference or code you use to identify this assessment (e.g. RA-2026-001). Must be unique within your PTA.">Assessment ID</HelpLabel>
              <Input value={editing.assessment_ref ?? ""} disabled={isLocked}
                placeholder="e.g. RA-2026-001"
                onChange={(e) => setEditing({ ...editing, assessment_ref: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <HelpLabel tip="A detailed description of the activity or event this RA covers. Shown on the exported PDF/Word document above the hazards table.">Description</HelpLabel>
              <Textarea rows={3} value={editing.description ?? ""} disabled={isLocked}
                placeholder="Describe the activity, audience, scale, timings…"
                onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <HelpLabel tip="Choose whether this RA covers a one-off event, or a fixed/regular activity that rarely changes.">Type</HelpLabel>
              <Select value={editing.assessment_type ?? "event"} disabled={isLocked}
                onValueChange={(v) => setEditing({ ...editing, assessment_type: v as "event" | "generic" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">{ASSESSMENT_TYPE_LABEL.event}</SelectItem>
                  <SelectItem value="generic">{ASSESSMENT_TYPE_LABEL.generic}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <HelpLabel tip="Category of activity this RA covers. e.g. 'Summer Fair', 'BBQ', 'School Disco', 'Sponsored Walk'.">Activity type</HelpLabel>
              <Input value={editing.activity_type ?? ""} disabled={isLocked}
                placeholder="e.g. Summer Fair, BBQ, School Disco"
                onChange={(e) => setEditing({ ...editing, activity_type: e.target.value })} />
            </div>
            {editing.assessment_type === "event" && (
              <div>
                <HelpLabel tip="Link this RA to one of your events. The event date, name and location will appear on the exported PDF/Word document.">Linked event</HelpLabel>
                <Select value={editing.event_id ?? "none"} disabled={isLocked}
                  onValueChange={(v) => setEditing({ ...editing, event_id: v === "none" ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {events.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <HelpLabel side="left" tip="Where the activity will take place. e.g. 'School field', 'Main hall', 'Playground'.">Location</HelpLabel>
              <Input value={editing.location ?? ""} disabled={isLocked}
                onChange={(e) => setEditing({ ...editing, location: e.target.value })} />
            </div>
            <div>
              <HelpLabel tip="The date this RA was completed.">Assessment date</HelpLabel>
              <Input type="date" value={editing.assessment_date ?? ""} disabled={isLocked}
                onChange={(e) => setEditing({ ...editing, assessment_date: e.target.value || null })} />
            </div>
            {editing.assessment_type !== "event" && (
              <div>
                <HelpLabel side="left" tip="When this RA should next be reviewed. Typically 12 months from today, or before the next time you run the activity.">Review date</HelpLabel>
                <Input type="date" value={editing.review_date ?? ""} disabled={isLocked}
                  onChange={(e) => setEditing({ ...editing, review_date: e.target.value || null })} />
              </div>
            )}
            <div className="md:col-span-2">
              <HelpLabel tip="The PTA member completing this risk assessment. Pick from your members list.">Assessor</HelpLabel>
              <Select
                value={editing.assessor_name ?? "none"}
                disabled={isLocked}
                onValueChange={(v) => setEditing({ ...editing, assessor_name: v === "none" ? null : v })}
              >
                <SelectTrigger><SelectValue placeholder="Select a PTA member" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Select —</SelectItem>
                  {members.map(m => (
                    <SelectItem key={m.id} value={m.full_name}>
                      {m.full_name}{m.role ? ` · ${m.role}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle>Hazards & scoring</CardTitle>
                <Badge variant="outline" className="font-normal">
                  {editing.assessment_type === "generic" ? "Generic RA" : "Event RA"}
                </Badge>
              </div>
              <CardDescription>Rate each hazard as Low, Medium or High.</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">

              <Button size="sm" variant="outline" disabled={isLocked || aiBusy} onClick={aiSuggest}>
                <Sparkles className="h-4 w-4 mr-1" /> {aiBusy ? "Thinking..." : "AI suggest"}
              </Button>
              <Button size="sm" disabled={isLocked} onClick={() => {
                const next = [...hazards, emptyHazard(hazards.length)];
                setHazards(next);
                setEditingHazardIndex(next.length - 1);
              }}>
                <Plus className="h-4 w-4 mr-1" /> Add hazard
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {hazards.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hazards yet. Click "Add hazard" or "AI suggest".</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      <TableHead>Hazard</TableHead>
                      <TableHead>Who at risk</TableHead>
                      <TableHead className="w-24">Inherent</TableHead>
                      <TableHead className="w-24">Residual</TableHead>
                      <TableHead>Responsible</TableHead>
                      <TableHead className="w-32 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hazards.map((h, i) => {
                      const score = h.likelihood * h.severity;
                      const rscore = h.residual_likelihood * h.residual_severity;
                      return (
                        <TableRow key={i}>
                          <TableCell className="text-muted-foreground align-top">{i + 1}</TableCell>
                          <TableCell className="max-w-[260px]">
                            <span className="line-clamp-2 text-sm" title={h.hazard}>
                              {h.hazard || <em className="text-muted-foreground">(empty)</em>}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            <span className="line-clamp-2 text-sm" title={h.who_at_risk}>
                              {h.who_at_risk || "—"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <RiskBadge value={score} size="sm" labelOnly />

                          </TableCell>
                          <TableCell>
                            <RiskBadge value={rscore} size="sm" labelOnly />
                          </TableCell>
                          <TableCell className="max-w-[140px]">
                            <span className="line-clamp-1 text-sm" title={h.action_owner}>{h.action_owner || "—"}</span>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" onClick={() => setEditingHazardIndex(i)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>View</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" disabled={isLocked}
                                    onClick={() => setHazards(hazards.filter((_, j) => j !== i))}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hazard detail modal */}
        <Dialog open={editingHazardIndex !== null} onOpenChange={(o) => !o && setEditingHazardIndex(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Hazard {editingHazardIndex !== null ? editingHazardIndex + 1 : ""}</DialogTitle>
              <DialogDescription>Rate each hazard as Low, Medium or High.</DialogDescription>
            </DialogHeader>
            {editingHazardIndex !== null && hazards[editingHazardIndex] && (() => {
              const i = editingHazardIndex;
              const h = hazards[i];
              const update = (patch: Partial<Hazard>) => {
                const next = [...hazards]; next[i] = { ...h, ...patch }; setHazards(next);
              };
              const bandToNum = (b: "Low" | "Medium" | "High") => b === "High" ? 5 : b === "Medium" ? 3 : 1;
              const numToBand = (n: number): "Low" | "Medium" | "High" =>
                n >= 5 ? "High" : n >= 3 ? "Medium" : "Low";
              const bandTrigger: Record<"Low" | "Medium" | "High", string> = {
                Low: "bg-green-100 text-green-800 border-green-300",
                Medium: "bg-amber-100 text-amber-800 border-amber-300",
                High: "bg-red-100 text-red-800 border-red-300",
              };
              const BandSelect = ({ band, onPick }: { band: "Low" | "Medium" | "High"; onPick: (b: "Low" | "Medium" | "High") => void }) => (
                <Select value={band} disabled={isLocked} onValueChange={(v) => onPick(v as any)}>
                  <SelectTrigger className={`font-semibold ${bandTrigger[band]}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low"><span className={`inline-block px-2 py-0.5 rounded border ${bandTrigger.Low}`}>Low</span></SelectItem>
                    <SelectItem value="Medium"><span className={`inline-block px-2 py-0.5 rounded border ${bandTrigger.Medium}`}>Medium</span></SelectItem>
                    <SelectItem value="High"><span className={`inline-block px-2 py-0.5 rounded border ${bandTrigger.High}`}>High</span></SelectItem>
                  </SelectContent>
                </Select>
              );
              const inhScore = h.likelihood * h.severity;
              const resScore = h.residual_likelihood * h.residual_severity;
              const inhBand = scoreBand(inhScore).label as "Low" | "Medium" | "High";
              return (
                <div className="space-y-4">
                  <div>
                    <HelpLabel tip="What could cause harm? Be specific. e.g. 'Trip hazard from trailing cables', 'Slips on wet flooring near drinks stall'.">Hazard</HelpLabel>
                    <Textarea rows={2} value={h.hazard} disabled={isLocked}
                      placeholder="e.g. Trip hazard from trailing cables"
                      onChange={(e) => update({ hazard: e.target.value })} />
                  </div>
                  <div>
                    <HelpLabel tip="Who could be harmed by this hazard? e.g. 'Children, parents, volunteers, members of the public, contractors'.">Who at risk</HelpLabel>
                    <Textarea rows={2} value={h.who_at_risk} disabled={isLocked}
                      placeholder="e.g. Children, parents, volunteers, public"
                      onChange={(e) => update({ who_at_risk: e.target.value })} />
                  </div>

                  {/* Inherent risk */}
                  <div className="rounded-md border bg-blue-50/40 p-3 space-y-3">
                    <div className="text-sm font-semibold text-blue-900">Inherent risk <span className="text-xs font-normal text-muted-foreground">(raw risk — how bad it could be)</span></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <HelpLabel tip="How likely is harm to occur? Low / Medium / High.">Likelihood</HelpLabel>
                        <BandSelect band={numToBand(h.likelihood)} onPick={(b) => update({ likelihood: bandToNum(b) })} />
                      </div>
                      <div>
                        <HelpLabel tip="How severe would the harm be if it happened? Low / Medium / High.">Severity</HelpLabel>
                        <BandSelect band={numToBand(h.severity)} onPick={(b) => update({ severity: bandToNum(b) })} />
                      </div>
                      <div>
                        <HelpLabel tip="Auto-calculated from Likelihood × Severity.">Score</HelpLabel>
                        <div className="pt-1"><RiskBadge value={inhScore} labelOnly /></div>
                      </div>
                    </div>
                    <div>
                      <HelpLabel tip="What controls reduce this risk? e.g. 'Cables taped down, area cordoned off, warning signage, briefed stewards'.">Controls</HelpLabel>
                      <Textarea rows={3} value={h.existing_controls} disabled={isLocked}
                        placeholder="e.g. Cables taped down, signage in place, stewards briefed"
                        onChange={(e) => update({ existing_controls: e.target.value })} />
                    </div>
                    {inhBand === "High" && (
                      <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-800">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>Inherent risk is rated <strong>High</strong>. It is advised that the event does not take place until better controls are in place to reduce the risk.</span>
                      </div>
                    )}
                  </div>

                  {/* Residual risk */}
                  <div className="rounded-md border bg-blue-50/60 p-3 space-y-3">
                    <div className="text-sm font-semibold text-blue-900">Residual risk <span className="text-xs font-normal text-muted-foreground">(risk left after controls are applied)</span></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <HelpLabel tip="Likelihood AFTER controls are applied.">Likelihood</HelpLabel>
                        <BandSelect band={numToBand(h.residual_likelihood)} onPick={(b) => update({ residual_likelihood: bandToNum(b) })} />
                      </div>
                      <div>
                        <HelpLabel tip="Severity AFTER controls are applied.">Severity</HelpLabel>
                        <BandSelect band={numToBand(h.residual_severity)} onPick={(b) => update({ residual_severity: bandToNum(b) })} />
                      </div>
                      <div>
                        <HelpLabel tip="Auto-calculated from Likelihood × Severity. Should normally be Low before the activity goes ahead.">Score</HelpLabel>
                        <div className="pt-1"><RiskBadge value={resScore} labelOnly /></div>
                      </div>
                    </div>
                    <div>
                      <HelpLabel tip="EXTRA actions to reduce risk further. e.g. 'Add second steward', 'Provide cable ramps', 'Test PA equipment on the morning'.">Additional controls</HelpLabel>
                      <Textarea rows={3} value={h.additional_controls} disabled={isLocked}
                        placeholder="e.g. Add second steward, provide cable ramps"
                        onChange={(e) => update({ additional_controls: e.target.value })} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <HelpLabel tip="Person responsible for this risk — making sure these controls are in place on the day. e.g. 'Sarah Jones – Events Lead'.">Responsible</HelpLabel>
                      <Input value={h.action_owner} disabled={isLocked}
                        placeholder="e.g. Sarah Jones"
                        onChange={(e) => update({ action_owner: e.target.value })} />
                    </div>
                    <div>
                      <HelpLabel side="left" tip="Deadline for any additional controls to be completed. Usually before the event date.">Action due date</HelpLabel>
                      <Input type="date" value={h.action_due_date ?? ""} disabled={isLocked}
                        onChange={(e) => update({ action_due_date: e.target.value || null })} />
                    </div>
                  </div>



                </div>
              );
            })()}
            <DialogFooter>
              <Button onClick={() => setEditingHazardIndex(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        <Card>
          <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            <div>
              <Label>General notes</Label>
              <Textarea rows={5} value={editing.general_notes ?? ""} disabled={isLocked}
                onChange={(e) => setEditing({ ...editing, general_notes: e.target.value })} />
            </div>
            <div>
              <Label>Emergency procedures</Label>
              <Textarea rows={5} value={editing.emergency_procedures ?? ""} disabled={isLocked}
                onChange={(e) => setEditing({ ...editing, emergency_procedures: e.target.value })} />
            </div>
            <div>
              <Label>First aid</Label>
              <Textarea rows={5} value={editing.first_aid_notes ?? ""} disabled={isLocked}
                onChange={(e) => setEditing({ ...editing, first_aid_notes: e.target.value })} />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================
  // LIBRARY + TEMPLATES VIEW
  // ============================================================
  return (
    <div className="space-y-4 animate-fade-in">
      <Tabs defaultValue="library" onValueChange={(v) => { if (v === "templates") loadTemplates(); }}>
        <TabsList>
          <TabsTrigger value="library">Library</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="space-y-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Risk Assessments</CardTitle>
                <CardDescription>Plan safer events — create, score and approve assessments.</CardDescription>
              </div>
              <Button onClick={createBlank}><Plus className="h-4 w-4 mr-1" /> New blank</Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : assessments.length === 0 ? (
                <p className="text-sm text-muted-foreground">No risk assessments yet. Start from a template or click "New blank".</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Activity</TableHead>
                      {requireRaApproval && <TableHead>Status</TableHead>}
                      <TableHead>Review date</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead className="w-40 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assessments.map(a => (
                      <TableRow key={a.id}>
                        <TableCell className="font-medium">{a.title}</TableCell>
                        <TableCell>{a.activity_type ?? "—"}</TableCell>
                        {requireRaApproval && (
                          <TableCell><Badge variant="outline">{a.status.replace("_", " ")}</Badge></TableCell>
                        )}
                        <TableCell>{a.review_date ?? "—"}</TableCell>
                        <TableCell>{new Date(a.updated_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => openEditor(a)}><Pencil className="h-4 w-4" /></Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => duplicateAssessment(a)}><Copy className="h-4 w-4" /></Button>
                              </TooltipTrigger>
                              <TooltipContent>Duplicate</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => setConfirmDeleteId(a.id)}><Trash2 className="h-4 w-4" /></Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>Ready-made templates</CardTitle>
              <CardDescription>Click a template to start a new risk assessment with common hazards pre-filled.</CardDescription>
            </CardHeader>
            <CardContent>
              {templatesLoading ? (
                <p className="text-sm text-muted-foreground">Loading templates…</p>
              ) : templates.length === 0 ? (
                <p className="text-sm text-muted-foreground">No templates available yet.</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {templates.map(t => (
                    <button key={t.id} onClick={() => createFromTemplate(t.id)}
                      className="text-left rounded-lg border bg-card p-4 hover:border-primary transition-colors">
                      <div className="flex items-center gap-2 mb-1">
                        <FileTextIcon className="h-4 w-4 text-primary" />
                        <span className="font-medium">{t.name}</span>
                        {t.is_system && <Badge variant="secondary" className="ml-auto">System</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{t.description}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirmDeleteId} onOpenChange={(o) => !o && setConfirmDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete risk assessment?</DialogTitle>
            <DialogDescription>This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => confirmDeleteId && deleteAssessment(confirmDeleteId)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function escapeHtml(s: string | null | undefined) {
  if (!s) return "";
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
